import { Autocomplete, ClickAwayListener, Grid, Paper, Radio, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router';

import { CREATE, DataAPIContext } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import BlockRedirect from '../components/BlockRedirect';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';
import { eventOptions } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '1rem',
        minWidth: '300px',
        minHeight: '30vh',
        maxWidth: '500px',
    },
}));

const AddNewNotification = (props) => {
    const resource = 'notification';
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const classes = useStyles();
    const { hasPerm, isFranchise } = React.useContext(ContextStore);
    const [isFormSubmitted, setIsFormSubmitted] = React.useState(false)

    const [notification, setNotification] = React.useState({
        name: '',
        event: null,
        tour_override_mode: null,
        tours: [],
        delivery_method_ordering: 0,
        timedelta: 0,
        _changed: false,
    });

    const onLicenseeChange = (id) => {
        setNotification((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const franchiseValid = !isFranchise || notification.tour_operator;

    const createRef = React.useRef(null);
    const hasChanged = JSON.stringify({
        name: "",
        event: null,
        tour_override_mode: null,
        tours: [],
        delivery_method_ordering: 0,
        timedelta: 0,
        _changed: false
    }) !== JSON.stringify(notification)

    const dataAPI = React.useContext(DataAPIContext);

    const handleCreateNotification = async () => {
        props.setSyncActive(true);
        setIsFormSubmitted(true);
        disableMuiButton(createRef.current);
        (async () => {
            let createdId = null;
            try {
                const res = await dataAPI(CREATE, resource, { data: notification });
                console.log('create', res);
                if (res.statusCode === 201) {
                    enqueueSnackbar('Notification created', { variant: 'success' });
                    createdId = res.data?.id;
                    if (!createdId) {
                        props.setLastUpdate(new Date().toString());
                    }
                } else if (res.statusCode === 406 || res.statusCode === 400) {
                    enqueueSnackbar('Notification creation error', { variant: 'error' });
                }
            } finally {
                props.setSyncActive(false);
                if (createRef && createRef.current) {
                    enableMuiButton(createRef.current);
                }
                props.onClose(true);
            }
            // Like a new trip (CP-6207): open the new notification. After onClose, which on the
            // Notifications page pushes the list URL without ?creatingNotification.
            if (createdId) {
                history.push(`/notifications/${createdId}`);
            }
        })();
    };
    const onNotificationEventChange = (ev) => {
        setNotification((prev) => ({ ...prev, event: ev.target.value, _changed: true }));
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setNotification((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };

    const handleNotificationTypeChange = (ev, nt) => {
        if (!nt) {
            return;
        }
        setNotification((prev) => ({ ...prev, tour_override_mode: nt.value, _changed: true }));
    };

    const handleNotificationToursChange = (ev, tours) => {
        setNotification((prev) => ({ ...prev, tours: tours, _changed: true }));
    };

    const disableSaveButton = notification.tour_override_mode === null || notification.name.trim() === '' || !notification.event || !franchiseValid;

    if (!hasPerm('notification', 'write')) {
        <AccessDeniedView />;
    }

    return (
        <ClickAwayListener
            onClickAway={() => {
                // MUI Select/Autocomplete render menus in portals (outside this DOM tree).
                // If any overlay is mounted, the click was on a dropdown — don't close.
                if (document.querySelector('.MuiPopover-root, .MuiAutocomplete-popper, .MuiModal-root')) return;
                props.onClose();
            }}
        >
            <Paper className={classes.addTripUserPaperWrapper}>
                <FormLicenseeContext.Provider value={toLicenseeId(notification.tour_operator)}>
                    <BlockRedirect shouldBlock={hasChanged && !isFormSubmitted} message={'Are you sure you want to leave without saving changes?'} />
                    <Grid container justifyContent="space-between" direction="column">
                        <Grid className={classes.root} container>
                            <Grid item xs={12} container justifyContent="space-between">
                                <Typography variant="h6">Create New Notification</Typography>
                            </Grid>
                            <Grid item xs={12} mt={2}>
                                <LicenseeFormField value={notification.tour_operator} onChange={onLicenseeChange} allowParent={hasPerm('franchise', 'manager')} />
                            </Grid>
                            <Grid item xs={12} mt={2}>
                                <TextField label="Title" fullWidth variant="filled" value={notification.name} onChange={(ev) => setNotification((prev) => ({ ...prev, name: ev.target.value }))} />
                            </Grid>
                            <Grid item xs={12} mt={2}>
                                <Typography>Choose the topic for the notification you want to create:</Typography>{' '}
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={eventOptions}
                                    getOptionLabel={(option) => option.label}
                                    onChange={(event, value) => onNotificationEventChange({ target: { name: 'event', value: value ? value.value : '' } })}
                                    value={eventOptions.find((opt) => opt.value === notification.event) || null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Topic"
                                            variant="filled"
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} mt={2}>
                                <Typography>Choose the type of notification you want to create:</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={[
                                        { name: 'non_override', id: 0, label: 'Default', value: 0 },
                                        { name: 'override', id: 2, label: 'Tour Specific', value: 2 },
                                    ]}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={handleNotificationTypeChange}
                                    renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Type" />}
                                />
                            </Grid>
                            {(notification.tour_override_mode === 0 || notification.tour_override_mode === 1) && (
                                <Grid item xs={12} container>
                                    <Grid item container alignItems="center">
                                        <Radio value={0} name="non_override" checked={notification.tour_override_mode === 0} onClick={() => setNotification((prev) => ({ ...prev, tour_override_mode: 0 }))} />
                                        <Typography>Non overridable - Will be sent to all tours</Typography>
                                    </Grid>
                                    <Grid item container alignItems="center" justifyContent="flex-start">
                                        <Radio value={1} name="overridable" checked={notification.tour_override_mode === 1} onClick={() => setNotification((prev) => ({ ...prev, tour_override_mode: 1 }))} />
                                        <Typography>Overridable - Will be sent to all tours except the ones </Typography>
                                        <Typography>that have the same tour-specific notification</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {notification.tour_override_mode === 2 && (
                                <>
                                    <Grid item xs={12} mt={2}>
                                        <Typography>Please choose the tour(s) for the variant:</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RemoteAutocomplete
                                            filterSelected
                                            filterOptions={(options) => options}
                                            InputProps={{ required: true }}
                                            resource="tour"
                                            label="Select Tours:"
                                            multiple
                                            variant="filled"
                                            size="small"
                                            onChange={handleNotificationToursChange}
                                            value={notification.tours}
                                            getOptionLabel={(opt) => {
                                                if (!opt.is_bespoke) {
                                                    return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                                                }
                                                return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                                            }}
                                            renderOption={(props, option) => {
                                                if (option.is_bespoke) {
                                                    return (
                                                        <div {...props}>
                                                            [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div {...props}>
                                                        [id:{option.id}] {option.name} (Recurring)
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} mt={2}>
                                <Typography>Please choose one or more access tags:</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <AccessTagsDropdown value={notification.atag} onChange={onAccessTagsChange} />
                            </Grid>
                        </Grid>
                        <Grid item xs={12} p={2}>
                            <FormActionButtons
                                submitRef={createRef}
                                onSubmit={() => handleCreateNotification()}
                                submitDisabled={disableSaveButton}
                                onCancel={() => hasChanged ? props.onClose(false) : props.onClose(true)}
                                size="medium"
                            />
                        </Grid>
                    </Grid>
                </FormLicenseeContext.Provider>
            </Paper>
        </ClickAwayListener>
    );
};

export default AddNewNotification;
